import { ReactNode, useState } from 'react';
import { Card } from '../ui/Card';
import { Spinner } from '../ui/Spinner';
import { Button } from '../ui/Button';
import { ChevronLeft, ChevronRight } from 'lucide-react';

export interface Column<T> {
  key: string;
  header: ReactNode;
  render?: (item: T, index: number) => ReactNode;
  className?: string;
  mobileLabel?: string;
}

export interface DataTableProps<T> {
  columns: Column<T>[];
  data: T[];
  keyExtractor: (item: T, index: number) => string | number;
  isLoading?: boolean;
  emptyMessage?: ReactNode;
  className?: string;
  mobileCardRender?: (item: T, index: number) => ReactNode;
  pageSize?: number;
  onRowClick?: (item: T, index: number) => void;
  onReorder?: (newOrder: T[]) => void;
}

export function DataTable<T>({
  columns,
  data,
  keyExtractor,
  isLoading = false,
  emptyMessage = 'Nenhum registro encontrado.',
  className = '',
  mobileCardRender,
  pageSize,
  onRowClick,
  onReorder,
}: DataTableProps<T>) {
  const [currentPage, setCurrentPage] = useState(1);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  const totalPages = pageSize
    ? Math.max(1, Math.ceil(data.length / pageSize))
    : 1;
  const safePage = Math.min(currentPage, totalPages);
  const startIndex = pageSize ? (safePage - 1) * pageSize : 0;
  const currentData = pageSize
    ? data.slice(startIndex, startIndex + pageSize)
    : data;
  if (isLoading) {
    return (
      <Card className={className}>
        <Card.Body className="flex flex-col items-center justify-center space-y-3 py-12">
          <Spinner size="md" color="brand" />
          <p className="text-xs text-content-muted">Carregando dados...</p>
        </Card.Body>
      </Card>
    );
  }

  if (data.length === 0) {
    return (
      <Card className={className}>
        <Card.Body className="py-12 text-center text-xs text-content-muted">
          {emptyMessage}
        </Card.Body>
      </Card>
    );
  }

  return (
    <div className={`space-y-3 ${className}`}>
      {/* Visualização Mobile: Cards empilhados (< 640px) */}
      <div className="block space-y-2.5 sm:hidden">
        {currentData.map((item, index) => {
          const key = keyExtractor(item, startIndex + index);
          if (mobileCardRender) {
            return (
              <div key={key}>{mobileCardRender(item, startIndex + index)}</div>
            );
          }

          return (
            <Card key={key}>
              <Card.Body className="space-y-2 p-3.5">
                {columns.map((col) => {
                  const content = col.render
                    ? col.render(item, startIndex + index)
                    : ((item as Record<string, unknown>)[col.key] as ReactNode);
                  const label =
                    col.mobileLabel ||
                    (typeof col.header === 'string' ? col.header : col.key);

                  return (
                    <div
                      key={col.key}
                      className="flex items-center justify-between gap-2 text-xs"
                    >
                      <span className="flex-shrink-0 text-micro font-medium text-content-muted">
                        {label}
                      </span>
                      <div className="truncate text-right font-medium text-content-main">
                        {content}
                      </div>
                    </div>
                  );
                })}
              </Card.Body>
            </Card>
          );
        })}
      </div>

      {/* Visualização Desktop: Tabela estruturada (>= 640px) */}
      <div className="hidden sm:block">
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-left text-xs">
              <thead>
                <tr className="bg-surface-subtle/70 border-b border-surface-border text-content-muted">
                  {columns.map((col) => (
                    <th
                      key={col.key}
                      className={`px-4 py-3 text-micro font-semibold uppercase tracking-wider ${col.className || ''}`}
                    >
                      {col.header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-border">
                {currentData.map((item, index) => {
                  const globalIndex = startIndex + index;
                  const key = keyExtractor(item, globalIndex);
                  const isDragging = draggedIndex === globalIndex;
                  const isDragOver = dragOverIndex === globalIndex;

                  return (
                    <tr
                      key={key}
                      draggable={Boolean(onReorder)}
                      onDragStart={() => {
                        if (onReorder) setDraggedIndex(globalIndex);
                      }}
                      onDragOver={(e) => {
                        if (!onReorder) return;
                        e.preventDefault();
                        if (dragOverIndex !== globalIndex) {
                          setDragOverIndex(globalIndex);
                        }
                      }}
                      onDragLeave={() => {
                        if (dragOverIndex === globalIndex) {
                          setDragOverIndex(null);
                        }
                      }}
                      onDrop={(e) => {
                        if (!onReorder || draggedIndex === null) return;
                        e.preventDefault();
                        if (draggedIndex !== globalIndex) {
                          const updated = [...data];
                          const [moved] = updated.splice(draggedIndex, 1);
                          updated.splice(globalIndex, 0, moved);
                          onReorder(updated);
                        }
                        setDraggedIndex(null);
                        setDragOverIndex(null);
                      }}
                      onDragEnd={() => {
                        setDraggedIndex(null);
                        setDragOverIndex(null);
                      }}
                      onClick={(e) => {
                        if (!onRowClick) return;
                        // Evita disparar se o clique veio de input, button ou link
                        const target = e.target as HTMLElement | null;
                        if (
                          target?.closest('button') ||
                          target?.closest('input') ||
                          target?.closest('a')
                        ) {
                          return;
                        }
                        onRowClick(item, globalIndex);
                      }}
                      className={`transition-colors ${
                        onReorder ? 'cursor-grab active:cursor-grabbing' : ''
                      } ${
                        isDragging
                          ? 'bg-surface-subtle opacity-40'
                          : isDragOver
                            ? 'border-t-2 border-brand-500 bg-brand-500/10'
                            : onRowClick
                              ? 'hover:bg-surface-subtle/70 cursor-pointer'
                              : 'hover:bg-surface-subtle/50'
                      }`}
                    >
                      {columns.map((col) => {
                        const content = col.render
                          ? col.render(item, startIndex + index)
                          : ((item as Record<string, unknown>)[
                              col.key
                            ] as ReactNode);

                        return (
                          <td
                            key={col.key}
                            className={`px-4 py-3 text-content-main ${col.className || ''}`}
                          >
                            {content}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {/* Controles de Paginação */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-2 pt-1 text-xs text-content-muted">
          <span>
            Página{' '}
            <strong className="font-semibold text-content-main">
              {safePage}
            </strong>{' '}
            de{' '}
            <strong className="font-semibold text-content-main">
              {totalPages}
            </strong>{' '}
            ({data.length} registros)
          </span>

          <div className="flex items-center gap-1.5">
            <Button
              variant="secondary"
              size="sm"
              disabled={safePage <= 1}
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              leftIcon={<ChevronLeft className="h-3.5 w-3.5" />}
            >
              Anterior
            </Button>
            <Button
              variant="secondary"
              size="sm"
              disabled={safePage >= totalPages}
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              rightIcon={<ChevronRight className="h-3.5 w-3.5" />}
            >
              Próxima
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
